import { UserProfile } from '../contexts/UserProfileContext';

// 변수별 샘플 데이터
export const VARIABLE_SAMPLES: Record<string, string[]> = {
  '[이름]': ['홍길동', '김철수', '이영희', '박지민'],
  '[회사명]': ['테크스타트업', '혁신솔루션', '디지털플랫폼', '스마트컴퍼니'],
  '[직책]': ['대표이사', '팀장', '매니저', '개발자', '마케터'],
  '[업종]': ['IT/소프트웨어', '제조업', '서비스업', '교육', '의료/헬스케어'],
  '[사업단계]': ['아이디어 단계', '초기 창업', '성장 단계', '확장 단계'],
  '[사업유형]': ['온라인 플랫폼', 'SaaS 서비스', '모바일 앱', 'AI 솔루션'],
  '[타겟시장]': ['20-30대 직장인', '중소기업', '스타트업', '일반 소비자'],
  '[주요제품]': ['교육 플랫폼', '업무 자동화 툴', '건강관리 앱', '전자상거래 사이트'],
  '[예산규모]': ['1,000만원 이하', '1,000-5,000만원', '5,000만원-1억', '1억 이상'],
  '[사업일정]': ['3개월 이내', '6개월 이내', '1년 이내', '장기 계획'],
  '[경험배경]': ['IT 업계 5년 경험', '마케팅 전문가', '창업 경험 보유', '해당 분야 신입'],
  '[보유스킬]': ['개발, 기획', '마케팅, 영업', '디자인, 기획', '데이터 분석'],
  '[관심분야]': ['AI/머신러닝', '블록체인', '핀테크', '에듀테크'],
  '[사업목표]': ['매출 증대', '시장 점유율 확대', '브랜드 인지도 향상', '고객 만족도 개선'],
  '[금액]': ['100만원', '500만원', '1,000만원', '5,000만원'],
  '[타겟 고객]': ['20-30대 직장인', '중소기업 CEO', '학생', '주부'],
  '[제품/서비스 설명]': ['온라인 학습 플랫폼', '업무 효율성 도구', '헬스케어 앱', '쇼핑몰'],
};

// 프롬프트 변수 매핑
export const VARIABLE_MAPPINGS: Record<string, (profile: UserProfile) => string> = {
  // 기본 정보
  '[이름]': (profile) => profile.name || '[이름]',
  '[회사명]': (profile) => profile.company || '[회사명]',
  '[직책]': (profile) => profile.position || '[직책]',
  '[업종]': (profile) => profile.industry || '[업종]',
  '[사업단계]': (profile) => profile.businessStage || '[사업단계]',
  
  // 비즈니스 정보
  '[사업유형]': (profile) => profile.businessType || '[사업유형]',
  '[타겟시장]': (profile) => profile.targetMarket || '[타겟시장]',
  '[주요제품]': (profile) => profile.mainProduct || '[주요제품]',
  '[예산규모]': (profile) => profile.budget || '[예산규모]',
  '[사업일정]': (profile) => profile.timeline || '[사업일정]',
  '[경험배경]': (profile) => profile.experience || '[경험배경]',
  
  // 스킬과 관심사
  '[보유스킬]': (profile) => profile.skills.join(', ') || '[보유스킬]',
  '[관심분야]': (profile) => profile.interests.join(', ') || '[관심분야]',
  '[사업목표]': (profile) => profile.goals.join(', ') || '[사업목표]',
  
  // 공통 변수 (다양한 표현)
  '[반복된 행동/작업]': (profile) => {
    if (profile.skills.length > 0) {
      return `${profile.skills[0]} 관련 업무`;
    }
    return '[반복된 행동/작업]';
  },
  
  '[자주 맡은 일]': (profile) => {
    if (profile.experience) {
      return profile.experience;
    }
    if (profile.position) {
      return `${profile.position} 관련 업무`;
    }
    return '[자주 맡은 일]';
  },
  
  '[특정 관심 분야]': (profile) => {
    if (profile.interests.length > 0) {
      return profile.interests[0];
    }
    return '[특정 관심 분야]';
  },
  
  '[타겟 고객]': (profile) => profile.targetMarket || '[타겟 고객]',
  '[타깃 고객군]': (profile) => profile.targetMarket || '[타깃 고객군]',
  '[제품/서비스 설명]': (profile) => profile.mainProduct || '[제품/서비스 설명]',
  '[서비스 개요]': (profile) => profile.mainProduct || '[서비스 개요]',
  '[아이템 설명]': (profile) => profile.mainProduct || '[아이템 설명]',
  '[내 아이템 설명]': (profile) => profile.mainProduct || '[내 아이템 설명]',
  '[서비스명]': (profile) => profile.mainProduct || '[서비스명]',
  '[제품명]': (profile) => profile.mainProduct || '[제품명]',
  
  // 자원 관련
  '[보유 자원]': (profile) => {
    const resources = [];
    if (profile.skills.length > 0) {
      resources.push(`${profile.skills.join(', ')} 스킬`);
    }
    if (profile.experience) {
      resources.push(`${profile.experience} 경험`);
    }
    if (profile.budget) {
      resources.push(`${profile.budget} 예산`);
    }
    return resources.join(', ') || '[보유 자원]';
  },
  
  '[내 자원이나 조건]': (profile) => {
    const conditions = [];
    if (profile.skills.length > 0) {
      conditions.push(`${profile.skills.join(', ')} 가능`);
    }
    if (profile.budget) {
      conditions.push(`예산 ${profile.budget}`);
    }
    return conditions.join(', ') || '[내 자원이나 조건]';
  },
  
  // 브랜드 관련
  '[브랜드 핵심 메시지]': (profile) => {
    if (profile.goals.length > 0) {
      return profile.goals[0];
    }
    return '[브랜드 핵심 메시지]';
  },
  
  '[핵심 가치 키워드]': (profile) => {
    if (profile.goals.length > 0) {
      return profile.goals.join(', ');
    }
    return '[핵심 가치 키워드]';
  },
  
  // 예산 관련
  '[금액]': (profile) => profile.budget || '[금액]',
  '[초기 비용]': (profile) => profile.budget || '[초기 비용]',
  
  // 기타 공통 변수들
  '[현재 상황]': (profile) => {
    return `${profile.company || '회사'}에서 ${profile.position || '직책'}을 맡고 있으며, ${profile.businessStage || '사업단계'} 상태입니다.`;
  },
  
  '[전문 분야]': (profile) => {
    if (profile.skills.length > 0) {
      return profile.skills[0];
    }
    return profile.industry || '[전문 분야]';
  }
};

// 프롬프트에서 변수 추출
export const extractVariables = (content: string): string[] => {
  const regex = /\[([^\]]+)\]/g;
  const variables: string[] = [];
  let match;
  
  while ((match = regex.exec(content)) !== null) {
    variables.push(match[0]);
  }
  
  return Array.from(new Set(variables));
};

// 프롬프트 개인화
export const personalizePrompt = (content: string, profile: UserProfile): string => {
  let personalizedContent = content;
  
  // 각 변수에 대해 치환 실행
  Object.entries(VARIABLE_MAPPINGS).forEach(([variable, getValue]) => {
    const value = getValue(profile);
    // 원래 변수와 다른 값이 나온 경우에만 치환
    if (value !== variable) {
      const regex = new RegExp(escapeRegExp(variable), 'g');
      personalizedContent = personalizedContent.replace(regex, value);
    }
  });
  
  return personalizedContent;
};

// 정규식 이스케이프 함수
const escapeRegExp = (string: string): string => {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
};

// 프롬프트 완성도 계산
export const calculatePromptCompleteness = (content: string, profile: UserProfile): number => {
  const variables = extractVariables(content);
  const totalVariables = variables.length;
  
  if (totalVariables === 0) return 100;
  
  const replacedVariables = variables.filter(variable => {
    const getValue = VARIABLE_MAPPINGS[variable];
    if (getValue) {
      const result = getValue(profile);
      return result !== variable; // 치환되지 않은 경우 원래 변수와 동일
    }
    return false;
  });
  
  return Math.round((replacedVariables.length / totalVariables) * 100);
};

// 미완성 변수 목록 반환
export const getIncompleteVariables = (content: string, profile: UserProfile): string[] => {
  const variables = extractVariables(content);
  
  return variables.filter(variable => {
    const getValue = VARIABLE_MAPPINGS[variable];
    if (getValue) {
      const result = getValue(profile);
      return result === variable; // 치환되지 않은 경우
    }
    return true; // 매핑되지 않은 변수
  });
};

// 임시 값으로 프롬프트 개인화 (샘플 또는 사용자 입력값 사용)
export const personalizePromptWithTemp = (content: string, tempValues: Record<string, string>): string => {
  let personalizedContent = content;
  
  Object.entries(tempValues).forEach(([variable, value]) => {
    const regex = new RegExp(escapeRegExp(variable), 'g');
    personalizedContent = personalizedContent.replace(regex, value);
  });
  
  return personalizedContent;
};

// 프로필 기반 추천 프롬프트 필터링
export const getRecommendedPrompts = (prompts: any[], profile: UserProfile): any[] => {
  // 프로필의 관심사, 업종, 사업단계를 기반으로 추천
  return prompts.filter(prompt => {
    const content = prompt.content.toLowerCase();
    const tags = prompt.tags?.map((tag: string) => tag.toLowerCase()) || [];
    
    // 업종 매칭
    if (profile.industry) {
      const industry = profile.industry.toLowerCase();
      if (content.includes(industry) || tags.some((tag: string) => tag.includes(industry))) {
        return true;
      }
    }
    
    // 사업단계 매칭
    if (profile.businessStage) {
      const stage = profile.businessStage.toLowerCase();
      if (content.includes(stage) || tags.some((tag: string) => tag.includes(stage))) {
        return true;
      }
    }
    
    // 관심사 매칭
    if (profile.interests.length > 0) {
      const interests = profile.interests.map(interest => interest.toLowerCase());
      if (interests.some(interest => 
        content.includes(interest) || tags.some((tag: string) => tag.includes(interest))
      )) {
        return true;
      }
    }
    
    // 스킬 매칭
    if (profile.skills.length > 0) {
      const skills = profile.skills.map(skill => skill.toLowerCase());
      if (skills.some(skill => 
        content.includes(skill) || tags.some((tag: string) => tag.includes(skill))
      )) {
        return true;
      }
    }
    
    return false;
  });
};